import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "next-themes";

const Footer = () => {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Determine logo based on theme, default to color2 if not mounted or theme is light
  const logoSrc =
    mounted && resolvedTheme === "dark"
      ? "/logo/colored_white.png"
      : "/logo/black.png";

  return (
    <footer className="bg-white dark:bg-jacarta-900 border-t border-grayish/20 dark:border-jacarta-700 py-8">
      <div className="mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-2">
          {/* Logo Column */}
          <div className="md:col-span-2">
            <Link href="/" className="mb-4 inline-block">
              {mounted && (
                <Image
                  src={logoSrc}
                  alt="Impactoverse"
                  width={100}
                  height={40}
                  className="h-auto w-auto"
                />
              )}
            </Link>
          </div>

          {/* Explore Column */}
          <div className="md:col-span-2 md:ml-2">
            <h3 className="text-base font-medium mb-6 text-grayish dark:text-white">
              Explore
            </h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://ourworldofimpact.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  World of Impact
                </a>
              </li>
              <li>
                <Link
                  href="/campaigns"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  Impact Campaigns
                </Link>
              </li>
              <li>
                <Link
                  href="/about"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  About Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div className="md:col-span-2 md:ml-6">
            <h3 className="text-base font-medium mb-6 text-grayish dark:text-white">
              Support
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  href="/contact"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  FAQs
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  Reach Out
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Column */}
          <div className="md:col-span-3 md:ml-6">
            <h3 className="text-base font-medium mb-6 text-grayish dark:text-white">
              Legal
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  href="/privacy-policy"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  Cookies and Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  href="/terms"
                  className="text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent text-sm"
                >
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>

          {/* Get in Touch Section */}
          <div className="md:col-span-3">
            <h3 className="text-base font-medium mb-6 text-grayish dark:text-white">
              Get in Touch
            </h3>
            <div className="flex items-center space-x-3">
              <a
                href="mailto:info@impactoverse.com"
                aria-label="Email"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="18"
                  height="16"
                  viewBox="0 0 23 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M2.3 18C1.6675 18 1.12604 17.7797 0.675625 17.3391C0.225208 16.8984 0 16.3687 0 15.75V2.25C0 1.63125 0.225208 1.10156 0.675625 0.660938C1.12604 0.220313 1.6675 0 2.3 0H20.7C21.3325 0 21.874 0.220313 22.3244 0.660938C22.7748 1.10156 23 1.63125 23 2.25V15.75C23 16.3687 22.7748 16.8984 22.3244 17.3391C21.874 17.7797 21.3325 18 20.7 18H2.3ZM11.5 10.125L20.7 4.5V2.25L11.5 7.875L2.3 2.25V4.5L11.5 10.125Z" />
                </svg>
              </a>
              <a
                href="https://facebook.com/impactoverse"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M20.1214 10.0607C20.1214 4.50286 15.6186 0 10.0607 0C4.50286 0 0 4.50286 0 10.0607C0 15.0821 3.67929 19.2443 8.48857 20V12.9693H5.93286V10.0607H8.48857V7.84429C8.48857 5.32286 9.98929 3.93 12.2886 3.93C13.3893 3.93 14.5407 4.12643 14.5407 4.12643V6.60143H13.2714C12.0221 6.60143 11.6321 7.37714 11.6321 8.17286V10.0614H14.4221L13.9757 12.97H11.6314V20.0007C16.4414 19.245 20.12 15.0829 20.12 10.0614L20.1214 10.0607Z" />
                </svg>
              </a>
              <a
                href="https://twitter.com/impactoverse"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M8.7351 6.8513L14.199 0.5H12.9042L8.16 6.0149L4.3707 0.5H0L5.73 8.8394L0 15.5H1.2948L6.3048 9.6758L10.3068 15.5H14.6775L8.7345 6.8513H8.7351ZM6.9615 8.9129L6.381 8.0825L1.7613 1.4747H3.7503L7.4784 6.8072L8.0589 7.6376L12.9048 14.5694H10.9158L6.9615 8.9129Z" />
                </svg>
              </a>
              <a
                href="https://linkedin.com/company/impactoverse"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M0.677734 2.24242C0.677734 1.73737 0.854502 1.32071 1.20804 0.992424C1.56157 0.664141 2.02117 0.5 2.58683 0.5C3.14238 0.5 3.59188 0.661616 3.93531 0.984848C4.28885 1.31818 4.46561 1.75253 4.46561 2.28788C4.46561 2.77273 4.2939 3.17677 3.95046 3.5C3.59693 3.83333 3.13228 4 2.55652 4H2.54137C1.98582 4 1.53632 3.83333 1.19289 3.5C0.849452 3.16667 0.677734 2.74747 0.677734 2.24242ZM0.874704 15.5V5.37879H4.23834V15.5H0.874704ZM6.10198 15.5H9.46561V9.84849C9.46561 9.49495 9.50602 9.22222 9.58682 9.0303C9.72824 8.68687 9.94289 8.39647 10.2308 8.15909C10.5186 7.92172 10.8798 7.80303 11.3141 7.80303C12.4454 7.80303 13.0111 8.56566 13.0111 10.0909V15.5H16.3747V9.69697C16.3747 8.20202 16.0212 7.06818 15.3141 6.29545C14.607 5.52273 13.6727 5.13636 12.5111 5.13636C11.208 5.13636 10.1929 5.69697 9.46561 6.81818V6.84848H9.45046L9.46561 6.81818V5.37879H6.10198C6.12218 5.70202 6.13228 6.70707 6.13228 8.39394C6.13228 10.0808 6.12218 12.4495 6.10198 15.5Z" />
                </svg>
              </a>
              <a
                href="https://instagram.com/impactoverse"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M10.3609 4.88257C7.52804 4.88257 5.24304 7.16757 5.24304 10.0004C5.24304 12.8333 7.52804 15.1183 10.3609 15.1183C13.1938 15.1183 15.4788 12.8333 15.4788 10.0004C15.4788 7.16757 13.1938 4.88257 10.3609 4.88257ZM10.3609 13.3276C8.53018 13.3276 7.03375 11.8354 7.03375 10.0004C7.03375 8.16542 8.52589 6.67328 10.3609 6.67328C12.1959 6.67328 13.688 8.16542 13.688 10.0004C13.688 11.8354 12.1916 13.3276 10.3609 13.3276ZM16.8823 4.67328C16.8823 5.33685 16.348 5.86685 15.6888 5.86685C15.0252 5.86685 14.4952 5.33257 14.4952 4.67328C14.4952 4.014 15.0295 3.47971 15.6888 3.47971C16.348 3.47971 16.8823 4.014 16.8823 4.67328ZM20.2716 5.88471C20.1959 4.28542 19.8309 2.869 18.6595 1.70185C17.4923 0.53471 16.0759 0.16971 14.4766 0.0897098C12.8288 -0.0038616 7.88875 -0.0038616 6.24018 0.0897098C4.64518 0.165424 3.22875 0.530424 2.05732 1.69757C0.885893 2.86471 0.525179 4.28114 0.445179 5.88042C0.351607 7.52828 0.351607 12.4683 0.445179 14.1169C0.520893 15.7161 0.885893 17.1326 2.05732 18.2997C3.22875 19.4669 4.64089 19.8319 6.24018 19.9119C7.88804 20.0054 12.828 20.0054 14.4766 19.9119C16.0759 19.8361 17.4923 19.4711 18.6595 18.2997C19.8266 17.1326 20.1916 15.7161 20.2716 14.1169C20.3652 12.469 20.3652 7.53328 20.2716 5.88542V5.88471ZM18.1423 15.8847C17.7952 16.7576 17.1223 17.4304 16.2445 17.7826C14.9302 18.304 11.8123 18.1833 10.3602 18.1833C8.90804 18.1833 5.78589 18.299 4.47589 17.7826C3.60304 17.4354 2.93018 16.7626 2.57804 15.8847C2.05661 14.5704 2.17732 11.4526 2.17732 10.0004C2.17732 8.54828 2.06161 5.42614 2.57804 4.11614C2.92518 3.24328 3.59804 2.57042 4.47589 2.21828C5.79018 1.69685 8.90804 1.81757 10.3602 1.81757C11.8123 1.81757 14.9345 1.70185 16.2445 2.21828C17.1173 2.56542 17.7902 3.23828 18.1423 4.11614C18.6638 5.43042 18.543 8.54828 18.543 10.0004C18.543 11.4526 18.663 14.5747 18.1423 15.8847Z" />
                </svg>
              </a>
              <a
                href="https://tiktok.com/@impactoverse"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="text-gray-500 dark:text-gray-400 hover:text-accent dark:hover:text-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current text-cement dark:text-white/80 hover:text-accent dark:hover:text-accent"
                >
                  <path d="M17.8367 8.20391C16.1167 8.20819 14.4396 7.67176 13.041 6.67105V13.6525C13.0403 14.9453 12.6453 16.2075 11.9081 17.2696C11.171 18.3318 10.1274 19.1439 8.916 19.5968C7.70529 20.0496 6.38457 20.1225 5.131 19.8046C3.87743 19.4868 2.751 18.7939 1.90243 17.8189C1.05314 16.8439 0.52243 15.6325 0.380288 14.3475C0.238145 13.0625 0.491716 11.7646 1.10743 10.6275C1.72314 9.49034 2.671 8.56819 3.82457 7.98462C4.97814 7.40105 6.28243 7.18319 7.56314 7.36034V10.8718C6.97672 10.6875 6.34743 10.6932 5.76529 10.8875C5.18314 11.0818 4.676 11.456 4.31886 11.9553C3.961 12.4546 3.77029 13.0546 3.77457 13.6689C3.77886 14.2832 3.976 14.881 4.33957 15.376C4.70315 15.871 5.21386 16.2389 5.79886 16.4268C6.38386 16.6146 7.01314 16.6125 7.59672 16.4218C8.18029 16.2303 8.68886 15.8596 9.04957 15.3625C9.41029 14.8653 9.60457 14.266 9.60457 13.6518V0.00390625H13.0424C13.0403 0.293906 13.0646 0.583906 13.1153 0.869621C13.2346 1.50748 13.4831 2.11462 13.8453 2.65319C14.2074 3.19176 14.6753 3.65105 15.221 4.00319C15.9974 4.51676 16.9074 4.79033 17.8381 4.78962V8.20319L17.8367 8.20391Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
